import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, AddLink, Delete, ExpandMore, LinkOff, RemoveCircleOutline } from "@mui/icons-material";
import { alpha, Theme } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import axios from "../../../../api/axios";
import CustomModal from "../../../../components/ui/modal/modal.component";
import CustomDatePicker from "../../../../components/ui/customDatePicker/customDatePicker.component";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { CounterDetailsTable } from "../../../../components/settlements/settlementCreator/counterDetailsTable.component";
import creatorStyles from "../../../../components/settlements/settlementCreator/settlementCreator.module.css";
import EquipmentCounterForm from "../../../../components/ui/equipmentCounterForm/equipmentCounterForm.component";
import {
  CAN_ENTER_EVERY_COUNTER_VALUE_PERMISSION,
  LowCounterValueConfirmModal,
  LowCounterValueWarning,
  parseCounterNumericValue,
} from "../../../../components/ui/equipmentCounterForm/lowCounterValueWarning.component";
import { useHasPermission } from "../../../../contexts/auth";
import { ContractTermPrice } from "../../../../types/contracts";
import { SettlementExtraPosition } from "../../../../types/settlements";
import {
  formatCounterInputValue,
  formatCounterProvider,
  formatCounterValue,
  formatDateTime,
  normalizeCounterInputValue,
} from "../../../../utils";
import {
  contractTermHasScanPrice,
  findContractTermPriceId,
  renderEquipmentFullNameList,
} from "../../../../utils/settlementContractTerms";
import { createAndAssignSettlementCounter } from "../../../../utils/settlementCounterActions";
import { compareByCounterKind } from "../../../../utils/counterKindOrder";
import {
  ERROR_SNACKBAR,
  SETTLEMENTS_ENDPOINT,
  SETTLEMENT_STATUS_TO_COLOR,
  SUCCESS_SNACKBAR,
} from "../../../../consts";

const COUNTER_KIND_LABELS: Record<string, string> = {
  BP: "Black pages",
  CP: "Color pages",
  SP: "Scanned pages",
};

// Small colored circular badge behind the "newly attached"/"term ended"
// equipment icons - plain tinted icons alone read as too faint at a glance.
const statusIconBadgeSx = (color: "success" | "warning") => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  height: 26,
  borderRadius: "50%",
  color: `${color}.main`,
  bgcolor: (theme: Theme) => alpha(theme.palette[color].main, 0.15),
});

interface SettlementExtraPositionFormRow {
  tempId: string;
  id?: number;
  name: string;
  price: string;
}

interface CounterDetail {
  id: string;
  equipment_id?: string | number | null;
  kind: string;
  kind_display?: string;
  value?: string;
  read_at?: string;
  provider?: string;
  provider_display?: string;
  email_source_display?: string | null;
}

interface ProviderCounterOption {
  category: string;
  category_display: string;
  counter_detail: CounterDetail;
  // Whether this is the reading the position is actually using right now
  // (either the automatic pick or a manual override).
  is_current: boolean;
}

interface SettlementDetailPosition {
  id: string | number;
  price: string | number;
  counter: string | number | null;
  counter_detail: CounterDetail | null;
  previous_counter: string | number | null;
  previous_counter_detail: CounterDetail | null;
  // Falls back to the price's start_counter when there's no previous
  // settlement yet - always present, unlike previous_counter_detail.
  // Mirrors the fallback used by the sale-summary selector so both
  // views agree instead of one silently showing nothing.
  effective_previous_counter_detail: CounterDetail | null;
  investigation_reasons: string[];
  // Latest reading per EquipmentCounter provider category (User / Email
  // Report / PDF), so staff can pick whichever provider's reading to use.
  provider_counter_options?: ProviderCounterOption[];
  is_manual_override?: boolean;
}

interface SettlementDetailEquipment {
  id: string | number;
  full_name?: string;
  serial?: string;
}

interface SettlementDetail {
  id: string | number;
  status: string;
  status_display: string;
  sale_date?: string | null;
  total_overridden?: number | string | null;
  counter_priority_provider_display?: string | null;
  positions?: SettlementDetailPosition[];
  extra_positions?: SettlementExtraPosition[];
  contract_term?: {
    active?: boolean;
    client_name?: string | null;
    contract_info?: { number?: string; internal_name?: string } | null;
    equipments?: SettlementDetailEquipment[];
    prices?: ContractTermPrice[];
    prices_info?: ContractTermPrice[];
  };
  // True when no prior settlement exists yet for this term - its first-ever
  // settlement, i.e. newly attached to the contract.
  is_first_settlement?: boolean;
}

interface SettlementActionModalProps {
  settlementId: number | string | null;
  opened: boolean;
  setOpened: (value: boolean) => void;
}

interface CounterInputRow {
  key: string;
  kind: string;
  equipmentId: string | number | undefined;
  removable: boolean;
  previousValue?: number;
  previousDate?: string;
  // The ContractTermPrice this reading is meant to resolve, when known - lets
  // the saved value be pinned to this position regardless of the counter
  // priority provider (see createAndAssignSettlementCounter).
  price?: string | number;
}

export const SettlementActionModal = ({
  settlementId,
  opened,
  setOpened,
}: SettlementActionModalProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const canEnterEveryCounterValue = useHasPermission(CAN_ENTER_EVERY_COUNTER_VALUE_PERMISSION);

  const [settlement, setSettlement] = useState<SettlementDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [lowValueConfirmOpen, setLowValueConfirmOpen] = useState(false);
  const [lowValueWarnings, setLowValueWarnings] = useState<LowCounterValueWarning[]>([]);
  const [editingCounterId, setEditingCounterId] = useState<string | null>(null);

  const [counterValues, setCounterValues] = useState<Record<string, string>>({});
  const [adHocRows, setAdHocRows] = useState<CounterInputRow[]>([]);
  const adHocIdRef = useRef(0);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [overrideSubmittingKey, setOverrideSubmittingKey] = useState<string | null>(null);

  const [saleDate, setSaleDate] = useState("");
  const [totalOverridden, setTotalOverridden] = useState("");
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const [updateSubmitting, setUpdateSubmitting] = useState(false);

  const [extraPositions, setExtraPositions] = useState<SettlementExtraPositionFormRow[]>([]);
  const [extraPositionsSubmitting, setExtraPositionsSubmitting] = useState(false);
  const [extraPositionsError, setExtraPositionsError] = useState("");

  const createExtraPositionRow = useCallback(
    (position?: SettlementExtraPosition): SettlementExtraPositionFormRow => ({
      tempId: `extra-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      id: position?.id,
      name: position?.name || "",
      price:
        position?.price === null || position?.price === undefined
          ? ""
          : String(position.price),
    }),
    []
  );

  const buildExtraPositionRows = useCallback(
    (positions?: SettlementExtraPosition[]): SettlementExtraPositionFormRow[] => {
      if (!positions || positions.length === 0) {
        return [];
      }
      return positions.map((position) => createExtraPositionRow(position));
    },
    [createExtraPositionRow]
  );

  const fetchSettlement = useCallback(async () => {
    if (!settlementId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${SETTLEMENTS_ENDPOINT}${settlementId}/`);
      const data: SettlementDetail = response.data;
      setSettlement(data);
      setSaleDate(data.sale_date || dayjs().format("YYYY-MM-DD"));
      setTotalOverridden(
        data.total_overridden !== null && data.total_overridden !== undefined
          ? String(data.total_overridden)
          : ""
      );
      setExtraPositions(buildExtraPositionRows(data.extra_positions));
    } catch (err) {
      setError(t("Failed to load settlement"));
    } finally {
      setLoading(false);
    }
  }, [settlementId, t, buildExtraPositionRows]);

  useEffect(() => {
    if (opened) {
      setDirty(false);
      setAdHocRows([]);
      fetchSettlement();
    } else {
      setSettlement(null);
      setCounterValues({});
      setError(null);
      setExtraPositions([]);
      setExtraPositionsError("");
    }
  }, [opened, settlementId, fetchSettlement]);

  const handleModalSetOpened = (value: boolean) => {
    if (!value && dirty) {
      window.dispatchEvent(new Event("settlementUpdated"));
      window.dispatchEvent(new Event("equipmentCounterAdded"));
    }
    setOpened(value);
  };

  const positions = settlement?.positions || [];
  const equipments = settlement?.contract_term?.equipments || [];
  const fallbackEquipmentId = equipments.length === 1 ? equipments[0].id : undefined;
  const hasMultipleEquipments = equipments.length > 1;

  const counterActionPositions = positions.filter(
    (position) => !position.counter || (position.investigation_reasons || []).includes("O")
  );
  const readyToConfirm =
    settlement?.status === "W" &&
    positions.length > 0 &&
    positions.every((position) => position.counter) &&
    !positions.some((position) => (position.investigation_reasons || []).length > 0);

  const getEquipmentLabelById = (equipmentId?: string | number | null): string => {
    const match =
      equipmentId != null
        ? equipments.find((equipment) => String(equipment.id) === String(equipmentId))
        : undefined;
    return match?.full_name || t("Equipment");
  };

  const getPositionEquipmentId = (position: SettlementDetailPosition) =>
    position.counter_detail?.equipment_id ??
    position.effective_previous_counter_detail?.equipment_id ??
    fallbackEquipmentId;

  const getCounterKindLabel = (kind: string): string => t(COUNTER_KIND_LABELS[kind] || kind);

  const positionInputRows: CounterInputRow[] = counterActionPositions.map((position) => {
    const previous = position.effective_previous_counter_detail;
    return {
      key: String(position.id),
      kind: previous?.kind || position.counter_detail?.kind || "",
      equipmentId: getPositionEquipmentId(position),
      removable: false,
      previousValue:
        previous?.value !== undefined && previous?.value !== null
          ? Number(previous.value)
          : undefined,
      previousDate: previous?.read_at ? formatDateTime(previous.read_at) : undefined,
      price: position.price,
    };
  });
  const allInputRows: CounterInputRow[] = [...positionInputRows, ...adHocRows];

  // Counters are only ever addressed from this modal once a settlement has
  // actually flagged NEEDS_INVESTIGATION - a WAITING_FOR_CONFIRMATION
  // settlement is expected to already have everything it needs (Scan
  // included), so surfacing counter inputs there would be misleading.
  const canManageCounters = settlement?.status !== "W";

  const equipmentsNeedingScanButton = canManageCounters
    ? equipments.filter(
        (equipment) =>
          !allInputRows.some(
            (row) => row.kind === "SP" && String(row.equipmentId) === String(equipment.id)
          ) && contractTermHasScanPrice(settlement?.contract_term, equipment.id)
      )
    : [];

  const addScanRow = (equipmentId: string | number) => {
    adHocIdRef.current += 1;
    setAdHocRows((prev) => [
      ...prev,
      {
        key: `scan-${equipmentId}-${adHocIdRef.current}`,
        kind: "SP",
        equipmentId,
        removable: true,
        price: findContractTermPriceId(settlement?.contract_term, equipmentId, "SP"),
      },
    ]);
  };

  const removeAdHocRow = (key: string) => {
    setAdHocRows((prev) => prev.filter((row) => row.key !== key));
    setCounterValues((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const rowFieldLabel = (row: CounterInputRow) =>
    hasMultipleEquipments
      ? `${getEquipmentLabelById(row.equipmentId)} — ${getCounterKindLabel(row.kind)}`
      : getCounterKindLabel(row.kind);

  const previousCounterRows = positions
    .slice()
    .sort((a, b) =>
      compareByCounterKind(
        a.counter_detail?.kind || a.effective_previous_counter_detail?.kind,
        b.counter_detail?.kind || b.effective_previous_counter_detail?.kind
      )
    )
    .map((position) => {
      const previous = position.effective_previous_counter_detail;
      const current = position.counter_detail;
      const currentValue = current ? Number(current.value) : NaN;
      const previousValue = previous ? Number(previous.value) : NaN;
      const hasDifference = !Number.isNaN(currentValue) && !Number.isNaN(previousValue);

      return {
        id: current?.id,
        kind:
          current?.kind_display ||
          previous?.kind_display ||
          getCounterKindLabel((current?.kind || previous?.kind) as string),
        value: formatCounterValue(current?.value),
        previousValue: formatCounterValue(previous?.value),
        difference: hasDifference ? formatCounterValue(currentValue - previousValue) : "-",
        previousReadDate: previous?.read_at ? formatDateTime(previous.read_at) : "-",
        currentReadDate: current?.read_at ? formatDateTime(current.read_at) : "-",
        previousProvider: formatCounterProvider(
          previous?.provider_display,
          previous?.email_source_display
        ),
        previousProviderCode: previous?.provider || "-",
        currentProvider: formatCounterProvider(
          current?.provider_display,
          current?.email_source_display
        ),
        currentProviderCode: current?.provider || "-",
      };
    });

  const getPositionKindLabel = (position: SettlementDetailPosition): string => {
    const current = position.counter_detail;
    const previous = position.effective_previous_counter_detail;
    return (
      current?.kind_display ||
      previous?.kind_display ||
      getCounterKindLabel((current?.kind || previous?.kind) as string)
    );
  };

  // Positions where there's an actual choice to make between providers -
  // either more than one provider has a reading and at least one of them
  // isn't the one currently in use, or staff already picked one manually -
  // surfaced so it can be reviewed/changed without waiting for the priority
  // provider's reading.
  const positionsWithAlternatives = positions.filter(
    (position) =>
      (position.provider_counter_options || []).some((option) => !option.is_current) ||
      position.is_manual_override
  );

  const handleUseAlternativeCounter = async (position: SettlementDetailPosition, counterId: string) => {
    const key = `${position.id}-${counterId}`;
    setOverrideSubmittingKey(key);
    try {
      await axios.post(`${SETTLEMENTS_ENDPOINT}${settlementId}/override-position-counter/`, {
        price_id: position.price,
        counter_id: counterId,
      });
      enqueueSnackbar(t("Counter value saved"), SUCCESS_SNACKBAR);
      setDirty(true);
      await fetchSettlement();
    } catch (err: any) {
      const message = err?.response?.data?.message || t("Failed to use this counter value");
      enqueueSnackbar(message, ERROR_SNACKBAR);
    } finally {
      setOverrideSubmittingKey(null);
    }
  };

  const handleRestoreDefaultCounter = async (position: SettlementDetailPosition) => {
    const key = `${position.id}-restore-defaults`;
    setOverrideSubmittingKey(key);
    try {
      await axios.post(`${SETTLEMENTS_ENDPOINT}${settlementId}/override-position-counter/`, {
        price_id: position.price,
        counter_id: null,
      });
      enqueueSnackbar(t("Default counter selection restored"), SUCCESS_SNACKBAR);
      setDirty(true);
      await fetchSettlement();
    } catch (err: any) {
      const message = err?.response?.data?.message || t("Failed to restore default counter selection");
      enqueueSnackbar(message, ERROR_SNACKBAR);
    } finally {
      setOverrideSubmittingKey(null);
    }
  };

  const getRowsToSubmit = () =>
    allInputRows.filter((row) => (counterValues[row.key] || "").trim() !== "");

  const performBulkSaveCounters = async (rowsToSubmit: CounterInputRow[]) => {
    setBulkSubmitting(true);
    try {
      for (const row of rowsToSubmit) {
        if (!row.equipmentId || !settlementId) {
          continue;
        }
        // Pinned to its price (when known) so this explicit, manually
        // entered value resolves the investigation regardless of the
        // configured counter priority provider - see
        // createAndAssignSettlementCounter.
        await createAndAssignSettlementCounter({
          settlementId,
          equipmentId: row.equipmentId,
          kind: row.kind,
          value: counterValues[row.key],
          priceId: row.price,
        });
      }
      await axios.post(`${SETTLEMENTS_ENDPOINT}${settlementId}/refresh-calculation/`);
      enqueueSnackbar(t("Counter values saved"), SUCCESS_SNACKBAR);
      setCounterValues((prev) => {
        const next = { ...prev };
        rowsToSubmit.forEach((row) => delete next[row.key]);
        return next;
      });
      setAdHocRows((prev) => prev.filter((row) => !rowsToSubmit.some((r) => r.key === row.key)));
      setDirty(true);
      await fetchSettlement();
    } catch (err: any) {
      const message = err?.response?.data?.message || t("Failed to add counter value");
      enqueueSnackbar(message, ERROR_SNACKBAR);
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleBulkSaveCounters = () => {
    const rowsToSubmit = getRowsToSubmit();
    if (rowsToSubmit.length === 0) {
      enqueueSnackbar(t("Please enter at least one counter value"), ERROR_SNACKBAR);
      return;
    }
    if (rowsToSubmit.some((row) => !row.equipmentId || !row.kind)) {
      enqueueSnackbar(t("Equipment is required to add counter"), ERROR_SNACKBAR);
      return;
    }

    if (canEnterEveryCounterValue) {
      const warnings: LowCounterValueWarning[] = rowsToSubmit
        .map((row): LowCounterValueWarning | null => {
          const enteredValue = parseCounterNumericValue(counterValues[row.key]);
          const latestValue = parseCounterNumericValue(row.previousValue);
          if (enteredValue === null || latestValue === null || enteredValue >= latestValue) {
            return null;
          }
          return {
            key: row.key,
            label: getCounterKindLabel(row.kind),
            enteredValue,
            latestValue,
            latestDate: row.previousDate,
          };
        })
        .filter((warning): warning is LowCounterValueWarning => warning !== null);

      if (warnings.length > 0) {
        setLowValueWarnings(warnings);
        setLowValueConfirmOpen(true);
        return;
      }
    }

    void performBulkSaveCounters(rowsToSubmit);
  };

  const handleConfirmLowValueBulkSave = () => {
    void performBulkSaveCounters(getRowsToSubmit());
  };

  // Lets the user save sale_date (and re-run the calculation) as a step
  // separate from confirming - a plain sale_date PATCH (no status) still
  // triggers a full server-side recalculation, so this is just
  // handleConfirm's payload minus `status`, which also keeps any existing
  // total_overridden from being silently cleared by the update.
  const handleUpdate = async () => {
    if (!saleDate) {
      enqueueSnackbar(t("Sale date is required"), ERROR_SNACKBAR);
      return;
    }

    setUpdateSubmitting(true);
    try {
      await axios.patch(`${SETTLEMENTS_ENDPOINT}${settlementId}/`, {
        sale_date: saleDate,
        total_overridden: totalOverridden || null,
      });
      await fetchSettlement();
      setDirty(true);
    } catch (err) {
      enqueueSnackbar(t("Failed to update calculation"), ERROR_SNACKBAR);
    } finally {
      setUpdateSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!saleDate) {
      enqueueSnackbar(t("Sale date is required"), ERROR_SNACKBAR);
      return;
    }

    setConfirmSubmitting(true);
    try {
      await axios.patch(`${SETTLEMENTS_ENDPOINT}${settlementId}/`, {
        status: "C",
        sale_date: saleDate,
        total_overridden: totalOverridden || null,
      });
      enqueueSnackbar(t("Settlement confirmed"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("settlementUpdated"));
      window.dispatchEvent(new Event("settlementConfirmed"));
      setOpened(false);
    } catch (err) {
      enqueueSnackbar(t("Failed to confirm settlement"), ERROR_SNACKBAR);
    } finally {
      setConfirmSubmitting(false);
    }
  };

  const normalizeExtraPositionsPayload = (positions: SettlementExtraPositionFormRow[]) => {
    const normalized: SettlementExtraPosition[] = [];

    for (const position of positions) {
      const name = position.name.trim();
      const rawPrice = position.price.trim();

      if (!name && !rawPrice) {
        continue;
      }

      if (!name || !rawPrice) {
        return {
          isValid: false,
          error: t("Please complete both name and price for each extra position"),
          payload: [] as SettlementExtraPosition[],
        };
      }

      const normalizedPrice = rawPrice.replace(",", ".");
      if (!/^\d+(\.\d{1,4})?$/.test(normalizedPrice)) {
        return {
          isValid: false,
          error: t("Invalid extra position price"),
          payload: [] as SettlementExtraPosition[],
        };
      }

      normalized.push({
        ...(position.id ? { id: position.id } : {}),
        name,
        price: normalizedPrice,
      });
    }

    return {
      isValid: true,
      error: "",
      payload: normalized,
    };
  };

  const handleAddExtraPositionRow = () => {
    setExtraPositionsError("");
    setExtraPositions((prev) => [...prev, createExtraPositionRow()]);
  };

  const handleRemoveExtraPositionRow = (rowTempId: string) => {
    setExtraPositionsError("");
    setExtraPositions((prev) => prev.filter((position) => position.tempId !== rowTempId));
  };

  const handleExtraPositionValueChange = (
    rowTempId: string,
    field: "name" | "price",
    value: string
  ) => {
    setExtraPositionsError("");
    setExtraPositions((prev) =>
      prev.map((position) =>
        position.tempId === rowTempId ? { ...position, [field]: value } : position
      )
    );
  };

  const handleSaveExtraPositions = async () => {
    const normalized = normalizeExtraPositionsPayload(extraPositions);

    if (!normalized.isValid) {
      setExtraPositionsError(normalized.error);
      return;
    }

    setExtraPositionsSubmitting(true);
    setExtraPositionsError("");

    try {
      const response = await axios.patch(`${SETTLEMENTS_ENDPOINT}${settlementId}/`, {
        extra_positions: normalized.payload,
      });

      const savedExtraPositions = response.data?.extra_positions || [];
      setExtraPositions(buildExtraPositionRows(savedExtraPositions));
      setSettlement((prev) => (prev ? { ...prev, extra_positions: savedExtraPositions } : prev));

      enqueueSnackbar(t("Extra positions saved"), SUCCESS_SNACKBAR);
      setDirty(true);
    } catch (err: any) {
      const responseData = err?.response?.data;
      const message =
        responseData?.message ||
        (typeof responseData === "string" ? responseData : t("Failed to save extra positions"));

      setExtraPositionsError(message);
      enqueueSnackbar(message, ERROR_SNACKBAR);
    } finally {
      setExtraPositionsSubmitting(false);
    }
  };

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {loading ? <CustomCircularProgress /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      {!loading && settlement ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="body2">
              <strong>{t("Client")}:</strong> {settlement.contract_term?.client_name || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>{t("Contract number")}:</strong>{" "}
              {[
                settlement.contract_term?.contract_info?.number,
                settlement.contract_term?.contract_info?.internal_name,
              ]
                .filter(Boolean)
                .join(" - ") || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>{t("Equipment")}:</strong>{" "}
              {renderEquipmentFullNameList(equipments, "-")}
            </Typography>
            <Typography variant="body2">
              <strong>{t("Counter priority provider")}:</strong>{" "}
              {settlement.counter_priority_provider_display || t("Not set")}
            </Typography>
          </Box>

          {previousCounterRows.length > 0 ? (
            <Paper variant="outlined" className={creatorStyles.previousCounterSection}>
              <CounterDetailsTable
                data={previousCounterRows}
                showEditButton
                onEdit={(counterId) => setEditingCounterId(counterId)}
              />
            </Paper>
          ) : null}

          {positionsWithAlternatives.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="subtitle2">{t("Counter readings by provider")}</Typography>
              <Paper variant="outlined" sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {hasMultipleEquipments && <TableCell>{t("Equipment")}</TableCell>}
                      <TableCell>{t("Kind")}</TableCell>
                      <TableCell>{t("Provider")}</TableCell>
                      <TableCell>{t("Value")}</TableCell>
                      <TableCell>{t("Read at")}</TableCell>
                      <TableCell align="right">{t("Action")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {positionsWithAlternatives.flatMap((position) =>
                      (position.provider_counter_options || []).map((option) => {
                        const restoreKey = `${position.id}-restore-defaults`;
                        const useKey = `${position.id}-${option.counter_detail.id}`;

                        return (
                          <TableRow
                            key={`${position.id}-${option.counter_detail.id}`}
                            sx={{ bgcolor: option.is_current ? "action.selected" : undefined }}
                          >
                            {hasMultipleEquipments && (
                              <TableCell>{getEquipmentLabelById(getPositionEquipmentId(position))}</TableCell>
                            )}
                            <TableCell>{getPositionKindLabel(position)}</TableCell>
                            <TableCell>{option.category_display}</TableCell>
                            <TableCell>{formatCounterValue(option.counter_detail.value)}</TableCell>
                            <TableCell>
                              {option.counter_detail.read_at
                                ? formatDateTime(option.counter_detail.read_at)
                                : "-"}
                            </TableCell>
                            <TableCell align="right">
                              {option.is_current ? (
                                position.is_manual_override ? (
                                  <Button
                                    size="small"
                                    color="inherit"
                                    disabled={overrideSubmittingKey !== null}
                                    onClick={() => handleRestoreDefaultCounter(position)}
                                  >
                                    {overrideSubmittingKey === restoreKey
                                      ? t("Processing...")
                                      : t("Restore defaults")}
                                  </Button>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    {t("Current")}
                                  </Typography>
                                )
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={overrideSubmittingKey !== null}
                                  onClick={() =>
                                    handleUseAlternativeCounter(position, option.counter_detail.id)
                                  }
                                >
                                  {overrideSubmittingKey === useKey
                                    ? t("Processing...")
                                    : t("Use this value")}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          ) : null}

          {canManageCounters &&
          (allInputRows.length > 0 || equipmentsNeedingScanButton.length > 0) ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography variant="subtitle2">{t("Counter values needed")}</Typography>

              {allInputRows.map((row) => (
                <Box
                  key={row.key}
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    alignItems: { xs: "stretch", sm: "center" },
                  }}
                >
                  <TextField
                    size="small"
                    label={rowFieldLabel(row)}
                    value={formatCounterInputValue(counterValues[row.key] || "")}
                    onChange={(event) => {
                      const normalizedValue = normalizeCounterInputValue(event.target.value);
                      if (normalizedValue === null) {
                        return;
                      }

                      setCounterValues((prev) => ({ ...prev, [row.key]: normalizedValue }));
                    }}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    sx={{ flex: 1, minWidth: { xs: "100%", sm: 220 } }}
                  />
                  {row.removable ? (
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={() => removeAdHocRow(row.key)}
                      aria-label={t("Remove")}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  ) : null}
                </Box>
              ))}

              {equipmentsNeedingScanButton.length > 0 ? (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {equipmentsNeedingScanButton.map((equipment) => (
                    <Button
                      key={equipment.id}
                      size="small"
                      variant="text"
                      startIcon={<Add fontSize="small" />}
                      onClick={() => addScanRow(equipment.id)}
                    >
                      {hasMultipleEquipments
                        ? t("Add Scan counter for {{equipment}}", {
                            equipment: equipment.full_name,
                          })
                        : t("Add Scan counter")}
                    </Button>
                  ))}
                </Box>
              ) : null}

              {allInputRows.length > 0 ? (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={bulkSubmitting}
                    onClick={handleBulkSaveCounters}
                  >
                    {bulkSubmitting
                      ? t("Processing...")
                      : t("Confirm counter values", { defaultValue: "Confirm" })}
                  </Button>
                </Box>
              ) : null}
            </Box>
          ) : null}

          {readyToConfirm ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography variant="subtitle2">{t("Confirm settlement")}</Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                <CustomDatePicker
                  label={t("Sale date")}
                  value={saleDate ? dayjs(saleDate) : null}
                  onChange={(value: Dayjs | null) =>
                    setSaleDate(value ? value.format("YYYY-MM-DD") : "")
                  }
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                  sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
                />
                <TextField
                  size="small"
                  label={t("Total Overridden")}
                  value={totalOverridden}
                  onChange={(event) => setTotalOverridden(event.target.value)}
                  sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={updateSubmitting || confirmSubmitting}
                  onClick={handleUpdate}
                >
                  {updateSubmitting ? t("Processing...") : t("Update")}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={confirmSubmitting || updateSubmitting}
                  onClick={handleConfirm}
                >
                  {confirmSubmitting ? t("Processing...") : t("Confirm")}
                </Button>
              </Box>
            </Box>
          ) : null}

          {(!canManageCounters || allInputRows.length === 0) &&
          !readyToConfirm ? (
            <Alert severity="info">{t("No action needed")}</Alert>
          ) : null}

          <Accordion
            disableGutters
            elevation={0}
            className={creatorStyles.extraPositionsAccordion}
            sx={{
              border: "1px solid #ccc",
              borderRadius: "8px !important",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2">{t("Extra positions")}</Typography>
            </AccordionSummary>
            <AccordionDetails className={creatorStyles.extraPositionsAccordionDetails}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {extraPositions.map((position) => (
                  <Box
                    key={position.tempId}
                    className={creatorStyles.extraPositionRow}
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                      alignItems: { xs: "stretch", sm: "center" },
                    }}
                  >
                    <TextField
                      label={t("Position name")}
                      size="small"
                      value={position.name}
                      onChange={(event) =>
                        handleExtraPositionValueChange(position.tempId, "name", event.target.value)
                      }
                      fullWidth
                    />
                    <TextField
                      label={t("Position price")}
                      size="small"
                      value={position.price}
                      onChange={(event) =>
                        handleExtraPositionValueChange(position.tempId, "price", event.target.value)
                      }
                      inputProps={{ inputMode: "decimal" }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveExtraPositionRow(position.tempId)}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  </Box>
                ))}

                {extraPositionsError ? (
                  <Typography variant="caption" color="error">
                    {extraPositionsError}
                  </Typography>
                ) : null}

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleAddExtraPositionRow}
                  >
                    {t("Add position")}
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    disabled={extraPositionsSubmitting}
                    onClick={handleSaveExtraPositions}
                  >
                    {extraPositionsSubmitting ? t("Processing...") : t("Save positions")}
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      ) : null}
    </Box>
  );

  const titleContent = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Box
        component="span"
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {t("Manage settlement")}
      </Box>
      {settlement ? (
        <Chip
          label={settlement.status_display}
          color={SETTLEMENT_STATUS_TO_COLOR[settlement.status] || "default"}
          size="small"
          sx={{ flexShrink: 0 }}
        />
      ) : null}
      {settlement?.is_first_settlement ? (
        <Tooltip
          title={t(
            "This equipment was newly attached to this contract - first settlement for it here"
          )}
        >
          <Box sx={statusIconBadgeSx("success")}>
            <AddLink fontSize="small" />
          </Box>
        </Tooltip>
      ) : null}
      {settlement?.contract_term?.active === false ? (
        <Tooltip title={t("This equipment's term has already been ended on this contract")}>
          <Box sx={statusIconBadgeSx("warning")}>
            <LinkOff fontSize="small" />
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  );

  return (
    <>
      <CustomModal
        opened={opened}
        setOpened={handleModalSetOpened}
        title={titleContent}
        width="min(560px, 92vw)"
        content={content}
      />

      <LowCounterValueConfirmModal
        opened={lowValueConfirmOpen}
        setOpened={setLowValueConfirmOpen}
        warnings={lowValueWarnings}
        onConfirm={handleConfirmLowValueBulkSave}
      />

      <CustomModal
        opened={Boolean(editingCounterId)}
        setOpened={(value) => {
          if (!value) {
            setEditingCounterId(null);
          }
        }}
        title={t("Edit counter value")}
        width="min(480px, 92vw)"
        content={
          editingCounterId ? (
            <EquipmentCounterForm
              id={editingCounterId}
              hiddenFields={["equipment_id", "job", "kind"]}
              onSuccess={() => {
                setEditingCounterId(null);
                setDirty(true);
                fetchSettlement();
              }}
            />
          ) : (
            <></>
          )
        }
      />
    </>
  );
};
